/**
 * RequireAuth — the authorisation gate, with no chrome attached to it.
 *
 * It used to be the first twenty lines of `DriverLayout`, which meant a route could have
 * the gate or have no gate, but could not have the gate WITHOUT the header, the nav and
 * the page padding. A signing ceremony needs exactly that: a signed-in driver, on a
 * full-screen page with nothing to wander off into mid-signature.
 *
 * Used two ways:
 *   · as a layout route element, gating an `<Outlet />` (the ceremony route);
 *   · wrapped around children (`DriverLayout` puts its own chrome inside it).
 *
 * Behaviour is unchanged from the version inside `DriverLayout`: spinner while auth
 * resolves, redirect to `/auth` once it has resolved unfavourably, and nothing rendered in
 * between — a child that mounts for one frame against a session that is about to be thrown
 * away is a child that fires a query as the wrong user.
 */

import { useEffect, type ReactNode } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';

export default function RequireAuth({ children }: { children?: ReactNode }) {
    const { user, isAuthorized, isLoading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isLoading && (!user || !isAuthorized)) {
            navigate('/auth');
        }
    }, [user, isAuthorized, isLoading, navigate]);

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!user || !isAuthorized) {
        return null;
    }

    return <>{children ?? <Outlet />}</>;
}
